import React, { useState } from 'react'

import Box from '@ilg-totallymoney/ui/components/Box'
import Flex from '@ilg-totallymoney/ui/components/Flex'
import ProductInformationBox from '@ilg-totallymoney/ui/productTable/components/ProductInformationBox'
import theme from '@ilg-totallymoney/ui/theme'

import { GuaranteedOfferType } from '@appTypes/App.interface'
import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

import { ContentProps } from '@components/CreditCardContent/components/Content.interface'
import getBalanceTransferContent from '@components/CreditCardContent/components/getBalanceTransferContent'
import getBestCardsContent from '@components/CreditCardContent/components/getBestCardsContent'
import getCashbackContent from '@components/CreditCardContent/components/getCashbackContent'
import getCombinedContent from '@components/CreditCardContent/components/getCombinedContent'
import getPoorCreditContent from '@components/CreditCardContent/components/getPoorCreditContent'
import getPurchaseContent from '@components/CreditCardContent/components/getPurchaseContent'
import getRewardsContent from '@components/CreditCardContent/components/getRewardsContent'
import getTravelContent from '@components/CreditCardContent/components/getTravelContent'
import CreditLimitModal from '@components/CreditLimitModal'
import GuaranteedOfferModal from '@components/GuaranteedOfferModal'
import IconTextLabel from '@components/IconTextLabel'

import useWindowSize from '@hooks/useWindowSize'

import getGuaranteedOfferType from '@utils/getGuaranteedOfferType'
import { getShowOfferDurationContent } from '@utils/getShowOfferDurationContent'

enum ModalType {
    CreditLimit = 'CreditLimit',
    GuaranteedOffer = 'GuaranteedOffer',
    None = 'None',
}

const getCardItemContents = ({ category, creditCard }: ContentProps) => {
    switch (category) {
        case CreditCardProductCategory.BalanceTransfer:
            return getBalanceTransferContent({ creditCard })
        case CreditCardProductCategory.Cashback:
            return getCashbackContent({ creditCard })
        case CreditCardProductCategory.Purchase:
            return getPurchaseContent({ creditCard })
        case CreditCardProductCategory.Travel:
            return getTravelContent({ creditCard })
        case CreditCardProductCategory.Combined:
            return getCombinedContent({ creditCard })
        case CreditCardProductCategory.Rewards:
            return getRewardsContent({ creditCard })
        case CreditCardProductCategory.PoorCredit:
            return getPoorCreditContent({ creditCard })
        case CreditCardProductCategory.BestCards:
        default:
            return getBestCardsContent({ creditCard })
    }
}

const CreditCardContent = ({ creditCard, category }: ContentProps) => {
    const { width } = useWindowSize()

    const [activeModal, setActiveModal] = useState<ModalType>(ModalType.None)

    const handleCloseModal = () => setActiveModal(ModalType.None)

    const cardItemContents = getCardItemContents({
        category,
        creditCard,
    }).filter(Boolean)

    const isTotallySureOffer =
        getGuaranteedOfferType({ creditCard }) ===
        GuaranteedOfferType.MarketingOffer

    if (width && width < theme.small) {
        const firstTwo = cardItemContents.slice(0, 2)
        const rest = cardItemContents.slice(2)

        return (
            <>
                <Flex gridGap="space05" mb="space10">
                    {firstTwo.map((cardItemContent, index) => {
                        const key = `${cardItemContent?.title}-${cardItemContent?.value}-${index}`

                        if (
                            cardItemContent?.detailType ===
                                'GuaranteedExample' ||
                            cardItemContent?.detailType ===
                                'GuaranteedCreditLimit'
                        ) {
                            return (
                                <Box key={key} flex={1}>
                                    <ProductInformationBox
                                        variant="guaranteed"
                                        text={cardItemContent?.title ?? ''}
                                        heading={cardItemContent?.value ?? ''}
                                        onClick={() =>
                                            setActiveModal(
                                                ModalType.GuaranteedOffer
                                            )
                                        }
                                    />
                                </Box>
                            )
                        }

                        if (cardItemContent?.detailType === 'application') {
                            return (
                                <Box key={key} flex={1}>
                                    <ProductInformationBox
                                        variant="enterGcl"
                                        onClick={() =>
                                            setActiveModal(
                                                ModalType.CreditLimit
                                            )
                                        }
                                    />
                                </Box>
                            )
                        }

                        return (
                            <Box key={key} flex={1}>
                                <ProductInformationBox
                                    variant="neutral"
                                    text={cardItemContent?.title ?? ''}
                                    heading={cardItemContent?.value ?? ''}
                                />
                            </Box>
                        )
                    })}
                </Flex>

                <Flex flexWrap="wrap" gridGap="space05" mt="space05">
                    {rest.map((cardItemContent, index) => {
                        const key = `${cardItemContent?.title}-${cardItemContent?.value}-${index}`

                        const isGuaranteed =
                            cardItemContent?.detailType ===
                                'GuaranteedExample' ||
                            cardItemContent?.detailType ===
                                'GuaranteedCreditLimit'

                        return (
                            <IconTextLabel
                                key={key}
                                isGuaranteed={isGuaranteed}
                                label={cardItemContent?.title ?? ''}
                                value={cardItemContent?.value ?? ''}
                                isLast={index === rest.length - 1}
                            />
                        )
                    })}
                </Flex>

                {activeModal === ModalType.CreditLimit && (
                    <CreditLimitModal
                        showModal
                        onModalClose={handleCloseModal}
                    />
                )}
                {activeModal === ModalType.GuaranteedOffer && (
                    <GuaranteedOfferModal
                        showModal
                        onModalClose={handleCloseModal}
                        offerType={getGuaranteedOfferType({ creditCard })}
                        showOfferDurationContent={
                            isTotallySureOffer
                                ? true
                                : getShowOfferDurationContent({
                                      category:
                                          category ??
                                          CreditCardProductCategory.BestCards,
                                      card: creditCard,
                                  })
                        }
                    />
                )}
            </>
        )
    }

    return (
        <>
            <Flex flexWrap="wrap" gridGap="space05">
                {cardItemContents.map((cardItemContent, index) => {
                    const key = `${cardItemContent?.title}-${cardItemContent?.value}-${index}`

                    if (
                        cardItemContent?.detailType === 'GuaranteedExample' ||
                        cardItemContent?.detailType === 'GuaranteedCreditLimit'
                    ) {
                        return (
                            <Box
                                key={key}
                                width={170}
                                style={{ whiteSpace: 'pre-line' }}
                            >
                                <ProductInformationBox
                                    variant="guaranteed"
                                    text={cardItemContent?.title ?? ''}
                                    heading={cardItemContent?.value ?? ''}
                                    onClick={() =>
                                        setActiveModal(
                                            ModalType.GuaranteedOffer
                                        )
                                    }
                                />
                            </Box>
                        )
                    }

                    if (cardItemContent?.detailType === 'application') {
                        return (
                            <Box key={key} width={170}>
                                <ProductInformationBox
                                    variant="enterGcl"
                                    onClick={() =>
                                        setActiveModal(ModalType.CreditLimit)
                                    }
                                />
                            </Box>
                        )
                    }

                    return (
                        <Box key={key} width={170}>
                            <ProductInformationBox
                                variant="neutral"
                                text={cardItemContent?.title ?? ''}
                                heading={cardItemContent?.value ?? ''}
                            />
                        </Box>
                    )
                })}
            </Flex>
            {activeModal === ModalType.CreditLimit && (
                <CreditLimitModal showModal onModalClose={handleCloseModal} />
            )}
            {activeModal === ModalType.GuaranteedOffer && (
                <GuaranteedOfferModal
                    showModal
                    onModalClose={handleCloseModal}
                    offerType={getGuaranteedOfferType({ creditCard })}
                    showOfferDurationContent={
                        isTotallySureOffer
                            ? true
                            : getShowOfferDurationContent({
                                  category:
                                      category ??
                                      CreditCardProductCategory.BestCards,
                                  card: creditCard,
                              })
                    }
                />
            )}
        </>
    )
}

export default CreditCardContent
